import type { ReactNode } from "react";
import { env } from "@/config/env";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import "./TableauMeetingDetails.css";
import type { TableauMeetingDetailsProps } from "@/types/draftCalendar";

const DEFAULT_TABLEAU_MEETING_DETAILS_URL =
  "https://tableau.internal.deloitte.com/views/MESDUCalendarWestlake/MeetingDetails?:embed=y#1";
const DEFAULT_MEETING_REQUEST_URL = "https://summit.deloitte.com/admin/meeting-request?type=1";
const DEFAULT_COS_DASHBOARD_URL = "https://summit.deloitte.com/approval-dashboard/non-summit-users/meetings";

interface ExternalReportLinkProps {
  className: string;
  href?: string;
  children: ReactNode;
}

function ExternalReportLink({ className, href, children }: ExternalReportLinkProps) {
  if (!href) {
    return (
      <button type="button" className={className} disabled title="External URL is not configured">
        {children}
      </button>
    );
  }

  return (
    <a className={className} href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

function formatFiscalYearLabel(fyYear: string | number) {
  const value = String(fyYear).trim();
  if (!value) return "";

  const match = value.match(/^(?:FY\s*)?(?:20)?(\d{2})$/i);

  return match ? `FY${match[1]}` : value;
}

export function TableauMeetingDetails({ FyYear }: TableauMeetingDetailsProps) {
  const currentUser = useCurrentUser();
  const roleCode = (currentUser?.roleCode ?? "").toLowerCase();
  const isBusinessApprover = roleCode === "business_approver";
  const tableauUrl = env.VITE_TABLEAU_MEETING_DETAILS_URL ?? DEFAULT_TABLEAU_MEETING_DETAILS_URL;
  const meetingRequestUrl = env.VITE_SUMMIT_MEETING_REQUEST_URL ?? DEFAULT_MEETING_REQUEST_URL;
  const cosDashboardUrl = env.VITE_COS_DASHBOARD_URL ?? DEFAULT_COS_DASHBOARD_URL;
  const fiscalYearLabel = formatFiscalYearLabel(FyYear);
  const calendarTitle = fiscalYearLabel
    ? `Deloitte University ${fiscalYearLabel} Calendar`
    : "Deloitte University Calendar";

  return (
    <main className="tableau-meeting-details-page" aria-labelledby="tableau-meeting-details-title">
      <div className="tableau-meeting-details-toolbar" role="toolbar" aria-label="Tableau meeting details actions">
        <h1 id="tableau-meeting-details-title">{calendarTitle}</h1>
        {isBusinessApprover && (
          <ExternalReportLink className="tableau-meeting-details-request-button" href={meetingRequestUrl}>
            Request Meeting
          </ExternalReportLink>
        )}
      </div>

      <section className="tableau-meeting-details-message" aria-label="Calendar finalized">
        <p>Congratulations, the DU calendar is finalized! To view your meetings, please click</p>
        <p>the link below to access the DU Calendar Report.</p>
      </section>

      <div className="tableau-meeting-details-actions">
        <ExternalReportLink className="tableau-meeting-details-cos-button" href={cosDashboardUrl}>
          COS Dashboard
        </ExternalReportLink>
        <ExternalReportLink className="tableau-meeting-details-report-button" href={tableauUrl}>
          View Report
        </ExternalReportLink>
      </div>
    </main>
  );
}